import { useState } from "react";
import { formatAmount } from "@/utils/formatAmount";
import {
  AlertTriangle,
  ArrowDownWideNarrow,
  CheckCircle2,
  Paperclip,
  Send,
  Trash2,
  TrendingDown,
} from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { ManagedFileLink } from "@/components/ui/ManagedFileLink";
import { Countdown } from "@/features/rfq/components/Countdown";
import { uploadAttachment } from "@/features/rfq/api/requests";
import { uploadErrorMessage } from "@/utils/managedFile";
import {
  useMyOffer,
  useSubmitOffer,
  useSubmitRunoffOffer,
  useWithdrawOffer,
  type PublicProceeding,
  type RunoffState,
} from "@/features/rfq/api/proceedings";

/**
 * Formularz oferty na stronie oferenta: złożenie, podgląd własnej oferty, wycofanie.
 *
 * Bez edycji z założenia — złożoną ofertę można tylko wycofać i złożyć nową (do
 * terminu). Po terminie oferta wiąże i nie da się jej ruszyć. Klauzula prawna jest
 * warunkiem złożenia; ankieta oświadczeń idzie z ogłoszenia.
 */

// Klauzula z ekranu składania — do przejrzenia przez prawnika, nie jest opinią prawną.
const OFFER_CLAUSE =
  "Oświadczam, że zapoznałem się z warunkami postępowania i je akceptuję. Oferta jest " +
  "wyrażona w PLN netto, obejmuje pełen zakres opisany w postępowaniu i wiąże przez 30 dni " +
  "od dnia zakończenia postępowania. Jestem świadomy, że złożenie oferty zobowiązuje mnie do przyjęcia i " +
  "wykonania zlecenia opisanego w warunkach postępowania — w zaoferowanej kwocie i terminie " +
  "- jeżeli moja oferta zostanie wybrana przez Zamawiającego. Przyjmuję do wiadomości, że " +
  "postępowanie nie stanowi oferty ani przetargu w rozumieniu Kodeksu cywilnego ani Prawa zamówień publicznych. Zamawiający " +
  "może je zamknąć bez wyboru którejkolwiek oferty, a oferty złożone poza platformą nie są " +
  "rozpatrywane.";

export function OfferPanel({ proceeding }: { proceeding: PublicProceeding }) {
  const { data: offer, isLoading } = useMyOffer(proceeding.id);
  const submit = useSubmitOffer(proceeding.id);
  const withdraw = useWithdrawOffer(proceeding.id);

  const [amount, setAmount] = useState("");
  const [answers, setAnswers] = useState<Record<string, boolean>>({});
  const [accepted, setAccepted] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  if (isLoading) return null;
  const open = proceeding.is_open_for_offers;

  // ── Złożona oferta ────────────────────────────────────────────────────────
  if (offer && offer.status === "submitted") {
    return (
      <div className="border-2 border-[var(--color-primary)] bg-white p-5 dark:bg-gray-800">
        <div className="flex flex-wrap items-center gap-2">
          <CheckCircle2
            size={18}
            className="text-green-600 dark:text-green-400"
          />
          <span className="text-lg font-semibold text-[var(--color-text-primary)]">
            Oferta złożona: {formatAmount(offer.amount_net)} zł netto
            {proceeding.price_basis && ` ${proceeding.price_basis}`}
          </span>
          <Badge color="green">{offer.status_label}</Badge>
        </div>
        <p className="mt-1 text-xs text-[var(--color-text-muted)]">
          Złożona {new Date(offer.submitted_at).toLocaleString("pl")}
          {/* Po dogrywce kwota nad spodem jest już inna niż ta z pierwszej tury —
              bez tej daty wygląda, jakby zmieniła się sama. */}
          {offer.runoff_submitted_at &&
            ` · obniżona ${new Date(offer.runoff_submitted_at).toLocaleString("pl")}`}{" "}
          · wiąże do {new Date(offer.binding_until).toLocaleDateString("pl")}
        </p>
        <p className="mt-2 text-xs text-amber-700 dark:text-amber-500">
          Uwaga: złożona oferta zobowiązuje do przyjęcia i wykonania zlecenia w
          zaoferowanej kwocie i terminie, jeśli zostanie wybrana.
        </p>
        {offer.attachments.length > 0 && (
          <div className="mt-4 border-t border-[var(--color-border)] pt-3">
            <p className="mb-2 font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
              Twoje załączniki
            </p>
            <ul className="space-y-1">
              {offer.attachments.map((attachment) => (
                <li key={attachment.id} className="flex flex-wrap items-center gap-2">
                  <ManagedFileLink file={attachment} icon={<Paperclip size={12} />}>
                    {attachment.original_filename}
                  </ManagedFileLink>
                </li>
              ))}
            </ul>
          </div>
        )}
        {Object.keys(offer.declaration_answers ?? {}).length > 0 && (
          <div className="mt-4 border-t border-[var(--color-border)] pt-3">
            <p className="mb-2 font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
              Twoje oświadczenia
            </p>
            <ul className="space-y-1">
              {Object.entries(offer.declaration_answers).map(([q, val]) => (
                <li
                  key={q}
                  className="flex items-center justify-between gap-3 text-sm"
                >
                  <span className="text-[var(--color-text-primary)]">{q}</span>
                  <Badge color={val ? "green" : "gray"}>
                    {val ? "TAK" : "NIE"}
                  </Badge>
                </li>
              ))}
            </ul>
          </div>
        )}
        {/* Dogrywka — jedyne okno po terminie, w którym wolno ruszyć własną kwotę. */}
        {proceeding.runoff && (
          <RunoffBox
            proceedingId={proceeding.id}
            runoff={proceeding.runoff}
            priceBasis={proceeding.price_basis}
          />
        )}

        {open ? (
          <div className="mt-4 border-t border-[var(--color-border)] pt-3">
            <p className="mb-2 text-xs text-[var(--color-text-muted)]">
              Oferty nie można edytować. Aby zmienić kwotę, wycofaj ją i złóż
              nową — do terminu składania.
            </p>
            <Button
              variant="secondary"
              onClick={() => {
                if (
                  confirm(
                    "Wycofać ofertę? Po wycofaniu możesz złożyć nową do terminu.",
                  )
                )
                  withdraw.mutate();
              }}
              disabled={withdraw.isPending}
            >
              <Trash2 size={14} className="mr-1.5 inline" /> Wycofaj ofertę
            </Button>
          </div>
        ) : (
          <p className="mt-4 border-t border-[var(--color-border)] pt-3 text-xs text-[var(--color-text-muted)]">
            Termin minął — oferta jest wiążąca i nie można jej już wycofać.
          </p>
        )}
      </div>
    );
  }

  // ── Po terminie, bez złożonej oferty ──────────────────────────────────────
  if (!open) {
    return (
      <div className="border border-[var(--color-border)] bg-white p-5 text-sm text-[var(--color-text-muted)] dark:bg-gray-800">
        Termin składania ofert minął.
        {offer?.status === "withdrawn" &&
          " Twoja oferta została wcześniej wycofana."}
      </div>
    );
  }

  // ── Formularz nowej oferty ────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!amount || Number(amount) <= 0)
      return setError("Podaj kwotę oferty (netto).");
    const missing = proceeding.declaration_questions.filter(
      (q) => !(q in answers),
    );
    if (missing.length) return setError("Odpowiedz na wszystkie oświadczenia.");
    if (!accepted) return setError("Zaznacz akceptację warunków postępowania.");
    setError("");
    // Pliki idą PRZED ofertą: gdyby wgrywanie padło, oferta nie zostaje złożona bez
    // kosztorysu, na który się w niej powołujemy.
    let ids: string[] = [];
    if (files.length) {
      setUploading(true);
      try {
        ids = await Promise.all(files.map(uploadAttachment));
      } catch (err) {
        setUploading(false);
        return setError(uploadErrorMessage(err, "Nie udało się wgrać załączników. Spróbuj ponownie."));
      }
      setUploading(false);
    }
    submit.mutate(
      {
        amount_net: amount,
        declaration_answers: answers,
        accepted_terms: accepted,
        attachments: ids,
      },
      {
        onError: (err: unknown) => {
          const detail = (
            err as { response?: { data?: { errors?: { detail?: string } } } }
          ).response?.data?.errors?.detail;
          setError(detail ?? "Nie udało się złożyć oferty.");
        },
      },
    );
  };

  return (
    <div className="border-2 border-[var(--color-primary)] bg-white p-5 dark:bg-gray-800">
      {offer?.status === "withdrawn" && (
        <p className="mb-3 flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-500">
          <AlertTriangle size={13} /> Poprzednia oferta została wycofana. Możesz
          złożyć nową.
        </p>
      )}

      <label className="mb-3 block">
        <span className="mb-1 block font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
          Kwota oferty (netto, PLN)
        </span>
        {/* Podstawa wyceny nad polem, nie pod nim: przy zleceniu „na 100 działek”
            decyduje o tym, jaką liczbę oferent w ogóle wpisze. */}
        {proceeding.price_basis && (
          <span className="mb-1 block text-sm font-semibold text-[var(--color-text-primary)]">
            Cena {proceeding.price_basis}
          </span>
        )}
        <input
          type="number"
          step="0.01"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="0,00"
          className="w-full max-w-xs border border-[var(--color-border)] bg-transparent px-3 py-2 text-sm text-[var(--color-text-primary)]"
        />
        {proceeding.max_price_net && (
          <span className="mt-1 block text-xs text-[var(--color-text-muted)]">
            Cena maksymalna: {formatAmount(proceeding.max_price_net)} zł netto —
            oferta wyższa nie zostanie przyjęta.
          </span>
        )}
      </label>

      {proceeding.declaration_questions.length > 0 && (
        <div className="mb-3">
          <p className="mb-1 font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
            Oświadczenia
          </p>
          <div className="space-y-1.5">
            {proceeding.declaration_questions.map((q) => (
              <div
                key={q}
                className="flex items-center justify-between gap-3 text-sm"
              >
                <span className="text-[var(--color-text-primary)]">{q}</span>
                <span className="flex shrink-0 gap-1">
                  {(["tak", "nie"] as const).map((opt) => {
                    const val = opt === "tak";
                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() =>
                          setAnswers((prev) => ({ ...prev, [q]: val }))
                        }
                        className={`border px-3 py-0.5 text-xs uppercase ${
                          answers[q] === val
                            ? "border-[var(--color-primary)] text-[var(--color-primary)]"
                            : "border-[var(--color-border)] text-[var(--color-text-muted)]"
                        }`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mb-3">
        <p className="mb-1 font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
          Załączniki do oferty (opcjonalnie)
        </p>
        <p className="mb-1.5 text-xs text-[var(--color-text-muted)]">
          Kosztorys, oferta w PDF, referencje — Zamawiający pobierze je razem z
          kwotą. Pliki sprawdza antywirus, więc do pobrania są chwilę po wysłaniu.
        </p>
        <FileDropZone
          multiple
          files={files}
          onFilesChange={setFiles}
          disabled={uploading || submit.isPending}
        />
      </div>

      <label className="mb-3 flex items-start gap-2 text-xs text-[var(--color-text-secondary)]">
        <input
          type="checkbox"
          className="mt-0.5 shrink-0"
          checked={accepted}
          onChange={(e) => setAccepted(e.target.checked)}
        />
        <span>{OFFER_CLAUSE}</span>
      </label>

      {error && (
        <p className="mb-3 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}

      {/* Bez akceptacji klauzuli oferta nie wiąże — przycisk ma być martwy, nie
          zwracać błąd po kliknięciu. */}
      <Button
        onClick={handleSubmit}
        disabled={submit.isPending || uploading || !accepted}
      >
        <Send size={14} className="mr-1.5 inline" />
        {uploading
          ? "Wgrywanie załączników…"
          : submit.isPending
            ? "Wysyłanie…"
            : "Złóż ofertę"}
      </Button>
      {!accepted && (
        <p className="mt-2 text-xs text-[var(--color-text-muted)]">
          Zaznacz akceptację warunków postępowania, aby złożyć ofertę.
        </p>
      )}
    </div>
  );
}

/**
 * Dogrywka oczami oferenta — druga i ostatnia tura.
 *
 * Wolno tu WYŁĄCZNIE zejść: pole nie przyjmie kwoty wyższej niż własna aktualna.
 * Świadomie nie pokazujemy ani liczby konkurentów, ani pozycji w rankingu — firma
 * decyduje na podstawie własnej ceny, a nie cudzych ruchów. Brak reakcji jest
 * dopuszczalny: wiąże wtedy oferta z pierwszej tury.
 */
function RunoffBox({
  proceedingId,
  runoff,
  priceBasis,
}: {
  proceedingId: string;
  runoff: RunoffState;
  priceBasis: string;
}) {
  const submit = useSubmitRunoffOffer(proceedingId);
  const [amount, setAmount] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  if (!runoff.participating) return null;

  if (!runoff.is_open) {
    return (
      <div className="mt-4 border-t border-[var(--color-border)] pt-3 text-xs text-[var(--color-text-muted)]">
        Termin dopytania minął{" "}
        {new Date(runoff.deadline).toLocaleString("pl")}
        {runoff.improved && " — Twoja obniżona kwota jest ofertą wiążącą."}
      </div>
    );
  }

  const ceiling = Number(runoff.max_amount_net ?? 0);
  // Walidacja NA BIEŻĄCO, nie dopiero po kliknięciu: reguła „tylko w dół" jest
  // nieoczywista, więc oferent ma zobaczyć problem w chwili wpisywania kwoty, a nie
  // po wysłaniu formularza. Serwer i tak sprawdza to jeszcze raz — to jest jedyne
  // miejsce, które naprawdę wiąże (patrz MyRunoffOfferView).
  const typed = amount.trim();
  const value = Number(typed.replace(",", "."));
  const invalid: string | null = !typed
    ? null
    : Number.isNaN(value) || value <= 0
      ? "Podaj kwotę w złotych (netto)."
      : value > ceiling
        ? `Kwota nie może być wyższa niż Twoja obecna oferta: ${formatAmount(
            runoff.max_amount_net ?? "0",
          )} zł netto. W dopytaniu można tylko zejść z ceny.`
        : null;

  const handle = async () => {
    if (!typed) return setError("Podaj nową kwotę (netto).");
    if (invalid) return setError(invalid);
    setError("");
    let ids: string[] = [];
    if (files.length) {
      setUploading(true);
      try {
        ids = await Promise.all(files.map(uploadAttachment));
      } catch (err) {
        setUploading(false);
        return setError(uploadErrorMessage(err, "Nie udało się wgrać załączników. Spróbuj ponownie."));
      }
      setUploading(false);
    }
    submit.mutate(
      { amount_net: amount, attachments: ids },
      {
        onSuccess: () => {
          setAmount("");
          setFiles([]);
        },
        onError: (err: unknown) => {
          const detail = (
            err as { response?: { data?: { errors?: { detail?: string } } } }
          ).response?.data?.errors?.detail;
          setError(detail ?? "Nie udało się obniżyć oferty.");
        },
      },
    );
  };

  return (
    <div className="mt-4 border-2 border-amber-400 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-950/30">
      <div className="flex flex-wrap items-center gap-2">
        <ArrowDownWideNarrow
          size={16}
          className="text-amber-700 dark:text-amber-400"
        />
        <span className="font-semibold text-[var(--color-text-primary)]">
          Dopytanie do złożonej oferty — możesz obniżyć kwotę
        </span>
        <Countdown deadline={runoff.deadline} />
      </div>
      <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
        Zamawiający wrócił do Państwa oferty i otworzył drugą, ostatnią turę.
        Możesz zejść do własnej kwoty albo niżej — podwyższenie nie jest możliwe.
        Nie ujawniamy ofert pozostałych firm ani Twojej pozycji. Jeśli nic nie
        zrobisz, wiąże Twoja dotychczasowa oferta.
      </p>

      {runoff.note && (
        <p className="mt-3 whitespace-pre-wrap border-l-2 border-amber-500 pl-2 text-sm text-[var(--color-text-primary)]">
          {runoff.note}
        </p>
      )}
      {runoff.my_note && (
        <div className="mt-3 border-l-2 border-[var(--color-primary)] pl-2">
          <p className="font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
            Pytanie Zamawiającego do Twojej oferty
          </p>
          <p className="whitespace-pre-wrap text-sm text-[var(--color-text-primary)]">
            {runoff.my_note}
          </p>
        </div>
      )}

      {runoff.improved && runoff.first_round_amount_net && (
        <p className="mt-3 flex items-center gap-1.5 text-sm text-green-700 dark:text-green-400">
          <TrendingDown size={14} />
          Obniżono z {formatAmount(runoff.first_round_amount_net)} zł do{" "}
          {formatAmount(runoff.max_amount_net!)} zł netto. Możesz zejść jeszcze
          niżej.
        </p>
      )}

      <label className="mt-3 block">
        <span className="mb-1 block font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
          Nowa kwota (netto, PLN){priceBasis && ` — cena ${priceBasis}`}
        </span>
        <input
          type="number"
          step="0.01"
          min="0.01"
          max={runoff.max_amount_net ?? undefined}
          value={amount}
          onChange={(e) => {
            setAmount(e.target.value);
            setError("");
          }}
          placeholder={runoff.max_amount_net ?? "0,00"}
          className={`w-full max-w-xs border bg-white px-3 py-2 text-sm text-[var(--color-text-primary)] dark:bg-gray-800 ${
            invalid
              ? "border-red-500"
              : "border-[var(--color-border)]"
          }`}
        />
        <span className="mt-1 block text-xs text-[var(--color-text-muted)]">
          Nie więcej niż {formatAmount(runoff.max_amount_net ?? "0")} zł —
          Twoja obecna oferta.
        </span>
      </label>

      <div className="mt-3">
        <p className="mb-1 font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
          Nowe załączniki (opcjonalnie)
        </p>
        <p className="mb-1.5 text-xs text-[var(--color-text-muted)]">
          Poprawiony kosztorys dojdzie do plików z pierwszej tury — nic nie znika.
        </p>
        <FileDropZone
          multiple
          files={files}
          onFilesChange={setFiles}
          disabled={uploading || submit.isPending}
        />
      </div>

      {(invalid || error) && (
        <p className="mt-2 text-sm text-red-600 dark:text-red-400">
          {invalid ?? error}
        </p>
      )}
      {/* Przycisk martwy przy złej kwocie — lepiej, żeby nie dało się kliknąć,
          niż żeby serwer odbijał to, co front i tak już wie. */}
      <Button
        className="mt-3"
        onClick={handle}
        disabled={submit.isPending || uploading || !typed || !!invalid}
      >
        <Send size={14} className="mr-1.5 inline" />
        {uploading
          ? "Wgrywanie załączników…"
          : submit.isPending
            ? "Wysyłanie…"
            : "Obniż ofertę"}
      </Button>
    </div>
  );
}
